import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, ArrowUp } from "lucide-react";

const SECTIONS = [
  {
    title: "Navigation",
    links: [
      { to: "/tradition", label: "Tradition" },
      { to: "/service", label: "Service" },
      { to: "/vespa-piaggio", label: "Vespa" },
      { to: "/pony-mofa", label: "Pony Mofa" },
      { to: "/zubehoer", label: "Zubehör" },
    ],
  },
  {
    title: "Casa Markus",
    links: [
      { href: "https://maps.google.com/?q=Hauptstrasse+74+4102+Binningen", label: "Hauptstrasse 74" },
      { href: "https://maps.google.com/?q=Hauptstrasse+74+4102+Binningen", label: "4102 Binningen · Hohle Gasse" },
      { href: "tel:+41614218833", label: "+41 61 421 88 33" },
      { href: "mailto:original@vespa-basel.ch", label: "original@vespa-basel.ch" },
    ],
  },
  {
    title: "Öffnungszeiten",
    links: [
      { to: "/casa-markus", label: "Di — Sa · 09 — 12" },
      { to: "/casa-markus", label: "Di — Fr · 15 — 18" },
      { to: "/casa-markus", label: "So / Mo · geschlossen" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="relative bg-[var(--anthracite)] text-[var(--cream)] pt-20 pb-10" data-testid="footer">
      <div className="tricolore absolute top-0 left-0 right-0 h-1" />
      <div className="container-wide">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div
              className="inline-flex items-stretch gap-4 bg-[var(--cream)] p-4 mb-6"
              data-testid="footer-logo-block"
            >
              <img
                src="/logo-vespa.png"
                alt="Vespa Basel — Kauf + Service Garantie"
                width="200"
                height="107"
                className="block object-contain"
              />
              <div className="flex flex-col justify-center text-[var(--anthracite)] border-l border-[var(--border)] pl-4">
                <p className="font-serif text-3xl tracking-tightest leading-none">
                  Vespa <span className="italic-accent text-[var(--sage-dark)]">Basel</span>
                </p>
                <p className="overline text-[10px] mt-2 text-[var(--muted-fg)]">
                  Casa Markus Zürcher · 1949
                </p>
              </div>
            </div>
            <p className="mt-2 max-w-md text-[var(--cream)]/70 leading-relaxed text-sm">
              Spezialist für Vespa und Pony Mofa in Basel / Binningen. Tradition, die fährt —
              Präzision, der man vertraut.
            </p>
            <div className="flex items-center gap-3 mt-8">
              <a href="https://www.instagram.com/" aria-label="Instagram" className="w-10 h-10 grid place-items-center border border-[var(--cream)]/30 hover:bg-[var(--sage-dark)] hover:border-[var(--sage-dark)] transition-colors" data-testid="social-instagram">
                <Instagram size={15} strokeWidth={1.4} />
              </a>
              <a href="https://www.facebook.com/" aria-label="Facebook" className="w-10 h-10 grid place-items-center border border-[var(--cream)]/30 hover:bg-[var(--sage-dark)] hover:border-[var(--sage-dark)] transition-colors" data-testid="social-facebook">
                <Facebook size={15} strokeWidth={1.4} />
              </a>
              <a href="tel:+41614218833" aria-label="Anrufen" className="w-10 h-10 grid place-items-center border border-[var(--cream)]/30 hover:bg-[var(--sage-dark)] hover:border-[var(--sage-dark)] transition-colors" data-testid="social-phone">
                <Phone size={15} strokeWidth={1.4} />
              </a>
              <a href="mailto:original@vespa-basel.ch" aria-label="E-Mail" className="w-10 h-10 grid place-items-center border border-[var(--cream)]/30 hover:bg-[var(--sage-dark)] hover:border-[var(--sage-dark)] transition-colors" data-testid="social-email">
                <Mail size={15} strokeWidth={1.4} />
              </a>
            </div>
          </div>

          {SECTIONS.map((s) => (
            <div key={s.title} className="lg:col-span-2">
              <p className="overline !text-[var(--sage-soft)] mb-5">{s.title}</p>
              <ul className="space-y-3 text-sm">
                {s.links.map((l) => (
                  <li key={l.label}>
                    {l.to ? (
                      <Link to={l.to} className="text-[var(--cream)]/80 hover:text-[var(--sage-soft)] transition-colors">
                        {l.label}
                      </Link>
                    ) : (
                      <a href={l.href} className="text-[var(--cream)]/80 hover:text-[var(--sage-soft)] transition-colors">
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-1 flex lg:justify-end items-start">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Nach oben"
              className="w-12 h-12 grid place-items-center border border-[var(--cream)]/30 hover:bg-[var(--cream)] hover:text-[var(--anthracite)] transition-all"
              data-testid="footer-back-to-top"
            >
              <ArrowUp size={16} strokeWidth={1.4} />
            </button>
          </div>
        </div>

        <div className="pt-8 border-t border-[var(--cream)]/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[11px] uppercase tracking-[0.2em] text-[var(--cream)]/50">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} Vespa Basel · Casa Markus Zürcher
          </p>
          <p>
            <Link to="/" className="hover:text-[var(--cream)]">Impressum</Link>
            <span className="mx-3">·</span>
            <Link to="/" className="hover:text-[var(--cream)]">Datenschutz</Link>
            <span className="mx-3">·</span>
            <span>Service Spezialist · Vespa · Pony</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
